import React from "react";
import { Plus, Trash } from "@phosphor-icons/react";
import { SIZE_UNITS, typesOf, subsOf, rowFor, workItemName, toIn, n3 } from "@/lib/gate";

const inp = "min-w-0 w-full h-10 bg-slate-800/60 border border-slate-600 text-white rounded-sm px-2 text-sm font-mono-num";
const DISC = { amount: "₹", percent: "%", per_sqft: "₹/sqft" };
const DISC_FIELD = { amount: "discount_amount", percent: "discount_percent", per_sqft: "discount_per_sqft" };

export const blankWorkItem = () => ({
  item_id: "", item_name: "", thickness: "", fab_rate_id: null, rate_per_sqft: "",
  width_in: "", height_in: "", quantity: 1, unit: "sqft", size_unit: "inch",
  sizing_mode: "exact", sizing_mode_w: "exact", sizing_mode_h: "exact", margin_inches: 0, margin_w_inches: 0, margin_h_inches: 0,
  discount_mode: "amount", discount_amount: 0, discount_percent: 0, discount_per_sqft: 0,
  work_item: { type_key: "", type_label: "", sub_key: "", sub_label: "", label: "", thickness_mm: "", size_unit: "inch" },
});

export const lineCalc = (it) => {
  const sqft = (toIn(it.width_in, it.size_unit) * toIn(it.height_in, it.size_unit) * (Number(it.quantity) || 0)) / 144;
  const gross = sqft * (Number(it.rate_per_sqft) || 0);
  const m = it.discount_mode || "amount";
  const d = m === "percent" ? (gross * (Number(it.discount_percent) || 0)) / 100
    : m === "per_sqft" ? sqft * (Number(it.discount_per_sqft) || 0) : Number(it.discount_amount) || 0;
  return { sqft, amount: Math.max(gross - Math.min(Math.max(d, 0), gross), 0) };
};

// Gates v2 work items live inside the order's `items` array (rows with `work_item`).
export default function GateWorkItems({ items, setItems, rateRows, columns }) {
  const cols = (columns || []).filter((c) => c.active);
  const types = typesOf(rateRows);
  const patch = (idx, p, wip) => setItems((all) => all.map((it, i) => {
    if (i !== idx) return it;
    const wi = { ...it.work_item, ...(wip || {}) };
    const next = { ...it, ...p, work_item: wi };
    if (wip && ("type_key" in wip || "sub_key" in wip || "thickness_mm" in wip)) {
      const row = rowFor(rateRows, wi.type_key, wi.sub_key);
      const r = row?.rates?.[wi.thickness_mm];
      next.fab_rate_id = row?.id || null;
      next.rate_per_sqft = r != null ? r : "";
    }
    next.work_item.size_unit = next.size_unit;
    next.item_name = workItemName(wi);
    return next;
  }));
  const pickType = (idx, key) => {
    const t = types.find((x) => x.key === key);
    const subs = subsOf(rateRows, key);
    patch(idx, {}, { type_key: key, type_label: t?.label || "", sub_key: subs[0]?.sub_key || "", sub_label: subs[0]?.sub_label || "" });
  };
  const list = items.map((it, idx) => ({ it, idx })).filter((x) => x.it.work_item);
  return (
    <div className="space-y-3" data-testid="gate-work-items">
      <div className="text-[10px] font-mono-num tracking-widest text-cyan-300">// WORK ITEMS — gate, railing, window… (same customer)</div>
      {list.map(({ it, idx }, n) => {
        const wi = it.work_item;
        const subs = subsOf(rateRows, wi.type_key);
        const { sqft, amount } = lineCalc(it);
        const dm = it.discount_mode || "amount";
        const rateEmpty = wi.type_key && wi.thickness_mm && it.rate_per_sqft === "";
        return (
          <div key={idx} className="surface-2 p-3 rounded-sm border-l-4 border-cyan-500/60 space-y-2" data-testid={`work-item-${n}`}>
            <div className="flex items-center gap-2">
              <span className="font-heading text-xs tracking-widest text-cyan-200">WORK ITEM {n + 1}</span>
              <span className="text-[11px] text-slate-400 truncate">{it.item_name}</span>
              <button type="button" onClick={() => setItems((a) => a.filter((_, i) => i !== idx))} className="ml-auto text-red-400 hover:text-red-300 p-1" data-testid={`work-item-remove-${n}`}><Trash size={16} /></button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <select value={wi.type_key} onChange={(e) => pickType(idx, e.target.value)} className={inp} data-testid={`work-item-type-${n}`}>
                <option value="">Type of work…</option>
                {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
              {subs.length > 0 ? (
                <select value={wi.sub_key} onChange={(e) => { const s = subs.find((x) => x.sub_key === e.target.value); patch(idx, {}, { sub_key: s?.sub_key || "", sub_label: s?.sub_label || "" }); }} className={inp} data-testid={`work-item-subtype-${n}`}>
                  {subs.map((s) => <option key={s.id} value={s.sub_key}>{s.sub_label}</option>)}
                </select>
              ) : <div className="text-[11px] text-slate-500 self-center">No sub-type</div>}
              <input value={wi.label} onChange={(e) => patch(idx, {}, { label: e.target.value })} placeholder="Label (Main gate…)" className={`${inp} md:col-span-2`} data-testid={`work-item-label-${n}`} />
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 items-center">
              <input type="number" min="0" value={it.width_in} onChange={(e) => patch(idx, { width_in: e.target.value })} placeholder="Width" className={inp} data-testid={`work-item-w-${n}`} />
              <input type="number" min="0" value={it.height_in} onChange={(e) => patch(idx, { height_in: e.target.value })} placeholder="Height" className={inp} data-testid={`work-item-h-${n}`} />
              <select value={it.size_unit} onChange={(e) => patch(idx, { size_unit: e.target.value })} className={inp} data-testid={`work-item-unit-${n}`}>
                {SIZE_UNITS.map((u) => <option key={u.key} value={u.key}>{u.label}</option>)}
              </select>
              <input type="number" min="1" value={it.quantity} onChange={(e) => patch(idx, { quantity: e.target.value })} placeholder="Qty" className={inp} data-testid={`work-item-qty-${n}`} />
              <div className="col-span-2 font-mono-num text-cyan-300 text-sm" data-testid={`work-item-sqft-${n}`}>{n3(Math.round(sqft * 1000) / 1000)} sqft</div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] font-mono-num text-slate-400 tracking-widest">PLATE</span>
              {cols.map((c) => (
                <button type="button" key={c.mm} onClick={() => patch(idx, {}, { thickness_mm: c.mm })}
                  className={`h-9 px-3 rounded-sm text-xs font-mono-num border ${wi.thickness_mm === c.mm ? "bg-cyan-500 text-slate-900 border-cyan-400" : "border-slate-600 text-slate-200"}`}
                  data-testid={`work-item-thk-${n}-${c.mm}`}>{c.mm}mm</button>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 items-center">
              <label className="text-[10px] font-mono-num text-slate-400">RATE ₹/SQFT
                <input type="number" min="0" value={it.rate_per_sqft} onChange={(e) => patch(idx, { rate_per_sqft: e.target.value })} className={`${inp} ${rateEmpty ? "border-amber-500" : ""}`} placeholder="Type rate" data-testid={`work-item-rate-${n}`} />
              </label>
              <label className="text-[10px] font-mono-num text-slate-400">DISCOUNT
                <div className="flex gap-1">
                  <button type="button" onClick={() => patch(idx, { discount_mode: dm === "amount" ? "percent" : dm === "percent" ? "per_sqft" : "amount" })} className="h-10 px-2 border border-slate-600 rounded-sm text-slate-200 text-xs" data-testid={`work-item-disc-mode-${n}`}>{DISC[dm]}</button>
                  <input type="number" min="0" value={it[DISC_FIELD[dm]] || ""} onChange={(e) => patch(idx, { [DISC_FIELD[dm]]: e.target.value })} className={inp} data-testid={`work-item-disc-${n}`} />
                </div>
              </label>
              <div className="md:col-span-2 text-right font-mono-num text-orange-400 text-lg font-black" data-testid={`work-item-amount-${n}`}>₹{(Math.round(amount * 100) / 100).toLocaleString("en-IN")}</div>
            </div>
            {rateEmpty && <div className="text-[11px] text-amber-300" data-testid={`work-item-rate-empty-${n}`}>Rate chart me ye rate khaali hai — rate type kariye.</div>}
          </div>
        );
      })}
      <button type="button" onClick={() => setItems((a) => [...a, blankWorkItem()])} className="h-10 px-4 border border-cyan-500/60 text-cyan-200 rounded-sm text-xs font-heading tracking-widest flex items-center gap-1" data-testid="add-work-item-btn"><Plus size={14} /> ADD WORK ITEM</button>
    </div>
  );
}
